import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { CorePlugin, PublicPluginAPI, SelectionContext } from '@atlaskit/editor-common/types';
import type { JSONNode } from '@atlaskit/editor-json-transformer/types';
import type { UserIntentPlugin } from '@atlaskit/editor-plugin-user-intent';
import type { RemixFeedbackMetadata } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import type { InteractionSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import type { TrackAIEventPayloads } from '@atlassian/conversation-assistant-instrumentation/types/analytics';
import type { StoreChatItem } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import type { StoreAssistant } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import type { ConversationAssistantConfig } from '@atlassian/conversation-assistant/types';
import type { SmartCreationContext } from '@atlassian/conversation-assistant/ui/staging-area-modal/SmartCreationContext';

import type { RemixHiddenOptionKind } from '../../types';
import type { RemixOptionKind, RemixOptionSelectionParameters } from '../remix/remix-overlay';

import type { ContentClassification } from './utils/content-classification';
export type { ContentClassification } from './utils/content-classification';

export type EditorApiWithCorePlugin = PublicPluginAPI<[CorePlugin, UserIntentPlugin]>;

export type EditorRemixLifecycleStartRequest =
	| {
			kind: 'create';
			/** When omitted, the lifecycle machine allocates the conversation identity. */
			conversationId?: string;
			targetNodeId?: string;
	  }
	| {
			kind: 'update';
			conversationId: string;
			targetNodeId: string;
			updateSentinel: string;
	  };

export type EditorRemixLifecycleEvent =
	| { type: 'PREVIEW_OPENED' }
	| { type: 'ACCEPTED' }
	| { type: 'DIRECT_INSERTION_STARTED' }
	| { type: 'INSERTED' }
	| { type: 'INSERTION_FAILED' }
	| { type: 'CANCELLED' };

export type EditorRemixLifecycleEndReason =
	| 'aborted'
	| 'cancelled'
	| 'error'
	| 'handed-off'
	/** Acknowledgement (handoff or insertion) never arrived; outcome unknown, released with no machine event. */
	| 'abandoned';

/**
 * Possession of a handle is the admission decision for this flow. Producers holding one are in
 * treatment and must not re-evaluate rollout controls for the flow's lifetime.
 */
export type EditorRemixLifecycleHandle = {
	conversationId: string;
	remixId: string;
	rollback: () => void;
	/** Returns true only when the machine accepted the event and changed phase. */
	transition?: (event: EditorRemixLifecycleEvent) => boolean;
	end?: (reason: EditorRemixLifecycleEndReason) => void;
};

export type EditorRemixLifecycleStartOutcome =
	| { status: 'disabled' }
	| { status: 'admitted'; handle: EditorRemixLifecycleHandle }
	| {
			/** Admission was rejected because another Remix flow is already active. */
			status: 'active-flow-rejected';
	  }
	| { status: 'shadow-infrastructure-failure' };

/**
 * The editor's lifecycle vocabulary, expressed without reference to the lifecycle machine.
 *
 * Both sides of the migration are projected into it: the editor's own signals via
 * `deriveLegacyEditorRemixPhase`, and the machine's phase via `deriveMachineEditorRemixPhase`. That
 * is what lets the two be compared without a second normalization of editor state.
 */
export type EditorRemixLifecyclePhase =
	| 'inactive'
	| 'thinking'
	| 'ready'
	| 'previewing'
	| 'inserting'
	| 'error'
	| 'aborted';

/**
 * @private
 * @deprecated Renamed to {@link EditorRemixLifecyclePhase} now that the machine phase projects into
 * the same vocabulary. No in-repo caller remains; retained only so a consumer pinned to an older
 * version of this package keeps compiling, and removable once the editor migration completes.
 */
export type EditorRemixLegacyPhase = EditorRemixLifecyclePhase;

export type EditorRemixLifecycleAdapter = {
	startFlow: (request: EditorRemixLifecycleStartRequest) => EditorRemixLifecycleStartOutcome;
	/**
	 * Reports the editor's own lifecycle phase so the product can compare it against the machine
	 * during migration shadow.
	 *
	 * Strictly observational: implementations must not influence the flow, and the editor ignores
	 * anything returned. Absent when the product does not observe parity, so products without an
	 * adapter emit nothing.
	 */
	observeLegacyPhase?: (legacyPhase: EditorRemixLifecyclePhase) => void;
};

export type InsertedContentIdentity = {
	attribute: 'id' | 'localId';
	value: string;
};

export type ScheduleRemixRetentionCheck = (options: {
	analyticsEvent: TrackAIEventPayloads;
	identity: InsertedContentIdentity;
	insertedAt?: number;
	interactionSource?: InteractionSource;
	receiverId: string;
}) => void;

export type DynamicUiSource = {
	contentId?: string;
	contentType: string;
	endIndex: number;
	endLocalId: string;
	fragmentAdf: JSONNode[] | null;
	fullAdf: DocNode;
	startIndex: number;
	startLocalId: string;
};

export type RemixModalMetadataLike = {
	[key: string]: unknown;
	aspectRatio?: string | null;
	imageStyle?: string | null;
	dynamicUiType?: RemixOptionKind;
	dynamicUiSubtype?: string;
	interactionSource?: InteractionSource;
	userPrompt?: string;
	onCancel?: () => void;
	aiFeatureName?: string;
	/**
	 * Anchor snapshot captured at click time by the entry point that opened this Remix flow. When
	 * `cc-ce-maui-remix-menu-multivariant` is enabled, this is forwarded to `RemixContainer` (the
	 * single convergence point for all Remix entry points) to render an anchored popup instead of
	 * the centered modal.
	 */
	anchorRect?: DOMRect;
	/** Popup placement to use alongside `anchorRect`. Defaults to `'bottom-start'` when omitted. */
	popupPlacement?: 'bottom-start' | 'bottom-end' | 'right-start';
	/**
	 * When set, the chooser skips its own picker UI and immediately starts the
	 * remix flow for this option type. Pairs with `preselectedSubType`.
	 */
	preselectedType?: RemixOptionKind;
	preselectedSubType?: string;
	/** Complete prompt submitted from an external Remix picker. */
	preselectedUserPrompt?: string;
	/** Conversation created before the native editor Remix flow starts. */
	preselectedConversationId?: string;
};

export type AddRemixHistoryEntryPayload = {
	conversationId: string;
	dynamicUiSource: DynamicUiSource;
	dynamicUiSubtype?: string;
	dynamicUiType: string;
	finalMessage: StoreAssistant;
	/** Classification captured from ACTION_CLASSIFICATION plus any content-specific stream data. */
	contentClassification?: ContentClassification;
	/** for image, there should be image fieldId */
	fieldId?: string;
	/** for MAUI extensions, there should be MAUI url */
	url?: string;
};

export type RemixHistoryEntry = {
	conversationId: string;
	dynamicUiSource: DynamicUiSource;
	latestFinalMessage: StoreAssistant;
	latestFinalMessageId?: string;
	stack: Array<{
		dynamicUiSubtype?: string;
		dynamicUiType: string;
		fieldId?: string;
		finalMessage: StoreAssistant;
		finalMessageId?: string;
		url?: string;
	}>;
};

export type RemixHistoryEntryForUpdate = {
	conversationId: string;
	dynamicUiSource: DynamicUiSource;
	latestFinalMessage?: StoreAssistant;
	latestFinalMessageId?: string;
	dynamicUiSubtype?: string;
	dynamicUiType: string;
	fieldId?: string;
	finalMessage: StoreAssistant;
	finalMessageId?: string;
	url?: string;
};

export type UpdateRemixImageHistoryFieldIdPayload = {
	conversationId: string;
	fieldId: string;
};

export type AddRemixHistoryStackEntryPayload = {
	conversationId: string;
	dynamicUiType: string;
	dynamicUiSubtype?: string;
	fieldId?: string;
	finalMessage: StoreAssistant;
	previousFieldId?: string;
	previousUrl?: string;
	url?: string;
};

/**
 * Dynamic-ui (MAUI) variant of {@link AddBackendRemixHistoryStackEntryPayload}.
 * Used when the bridge stream regenerates a MAUI app on the page.
 */
export type AddBackendMauiRemixHistoryStackEntryPayload = {
	/** The MAUI app id being replaced. */
	previousMauiId: string;
	/** New MAUI app URL synthesized from the BE-side new app id. */
	url: string;
	/** Inherits from the matched stack entry when omitted. */
	dynamicUiType?: string;
	/** Backend-confirmed subtype from a type-changing edit; inherited from the matched entry when omitted. */
	dynamicUiSubtype?: string;
};

/**
 * Image variant of {@link AddBackendRemixHistoryStackEntryPayload}.
 * Used when the bridge stream regenerates an image on the page.
 */
export type AddBackendImageRemixHistoryStackEntryPayload = {
	/** The media file id being replaced (BE-side previousMediaFileId). */
	previousFieldId: string;
	/** Live upload-side file id from onUploadAndInsertMedia's result. */
	fieldId: string;
};

/**
 * Payload for appending a stack entry from a backend-initiated remix update
 * (bridge stream). The reducer locates the existing entry by matching the
 * previous identifier against stack entries' MAUI app id (extracted from url)
 * or fieldId, then inherits `finalMessage`, `finalMessageId`, and (when omitted)
 * `dynamicUiType`/`dynamicUiSubtype` from the matched entry.
 *
 * Discriminated union of the dynamic-ui (MAUI) and image variants;
 * narrow with {@link isAddBackendMauiRemixHistoryStackEntryPayload}.
 */
export type AddBackendRemixHistoryStackEntryPayload =
	| AddBackendMauiRemixHistoryStackEntryPayload
	| AddBackendImageRemixHistoryStackEntryPayload;

/**
 * Type guard discriminating the dynamic-ui (MAUI) variant of
 * {@link AddBackendRemixHistoryStackEntryPayload} from the image variant.
 * The two branches have disjoint required fields (`previousMauiId` vs
 * `previousFieldId`), so an `in`-check on either is a safe discriminator.
 */
export function isAddBackendMauiRemixHistoryStackEntryPayload(
	payload: AddBackendRemixHistoryStackEntryPayload,
): payload is AddBackendMauiRemixHistoryStackEntryPayload {
	return 'previousMauiId' in payload;
}

/**
 * Props specific to the remix type.
 */
export type RemixTypeProps =
	| {
			remixType?: 'block';
	  }
	| {
			remixType: 'object';
			adfDocument: DocNode | Promise<DocNode>;
	  };

export type RemixContentComponentProps<
	TEditorApi extends EditorApiWithCorePlugin = EditorApiWithCorePlugin,
> = RemixTypeProps & {
	addHistoryEntry?: (options: AddRemixHistoryEntryPayload) => void;
	addHistoryStackEntry?: (options: AddRemixHistoryStackEntryPayload) => void;
	/** See `EditorRemixContext.onVariantUpdateComplete`. */
	onVariantUpdateComplete?: (id: string) => void;
	/** See `EditorRemixContext.onSubtypeMismatch`. */
	onSubtypeMismatch?: () => void;
	clearThinkingDecoration?: () => void;
	replaceWhiteboardEmbed?: (options: {
		contentId: string;
		finalizedUrl: string;
		localId?: string;
	}) => boolean | void;
	setMauiEmbedFinalizeStatus?: (options: {
		contentId: string;
		status: 'suppressed' | 'needs-finalize';
	}) => void;
	editorApi?: TEditorApi;
	handleModalClose: () => void;
	hiddenOptions?: readonly RemixHiddenOptionKind[];
	isComplete?: boolean;
	isModalOpen: boolean;
	isThinking: boolean;
	remixAttemptId?: string;
	isUpdate?: boolean;
	isViewMode?: boolean;
	historyEntryForUpdate?: RemixHistoryEntryForUpdate;
	dynamicUiSubtypeForUpdate?: string;
	showLocationPicker?: boolean;
	setThinkingComplete?: (isComplete: boolean) => void;
	finishRemixThinking?: () => void;
	onRemixThinkingCompleteAnimationFinished?: () => void;
	onStartThinking: (options?: { contentType?: string }) => void;
	onDismissThinkingUi?: (options?: { reopenModal?: boolean }) => void;
	/**
	 * `preserveRequest` suppresses only the abort of the in-flight AI request; the host still
	 * dismisses the legacy thinking UI. Used when a preview opens while generation may still be
	 * streaming into it. Hosts that do not understand the option ignore it.
	 */
	onStopThinking: (options?: { reopenModal?: boolean; preserveRequest?: boolean }) => void;
	onRegisterAbortRequest?: (abortFn: () => void) => () => void;
	setShowLocationPicker?: (showLocationPicker: boolean) => void;
	/** Registers a delayed acceptance check owned by the editor plugin lifecycle. */
	scheduleRemixRetentionCheck?: ScheduleRemixRetentionCheck;
	/**
	 * Product-supplied recovery handler invoked when automatic whiteboard
	 * finalization fails. Threaded into {@link RemixProductIntegration} by the
	 * product wrapper. See {@link RemixProductIntegration.onFinalizeFailed}.
	 */
	onFinalizeFailed?: (payload: RemixFinalizeFailedPayload) => boolean;
	remixModalMetadata?: RemixModalMetadataLike;
	remixSource?: string;
	selectedContext: SelectionContext;
};

export type RemixContentComponentFullProps<
	TEditorApi extends EditorApiWithCorePlugin = EditorApiWithCorePlugin,
> = RemixContentComponentProps<TEditorApi> & {
	editorRemixLifecycleAdapter?: EditorRemixLifecycleAdapter;
	entryPoint?: string;
	productConfig: RemixProductConfig;
	productIntegration: RemixProductIntegration;
	smartCreationConfig?: Pick<ConversationAssistantConfig, 'siteId' | 'userId' | 'product'>;
	smartCreationContext?: SmartCreationContext;
};

/**
 * Classified reason an external preview's editor insertion produced no positive acknowledgement.
 * Structurally identical to the Smart Creation capability's result; kept local on purpose so the
 * lifecycle does not depend on conversation-assistant types.
 */
export type ExternalPreviewInsertionNotAcknowledged = {
	insertionKind: 'embed' | 'image';
	outcome: 'rejected' | 'timed-out-cancelled' | 'timed-out-in-flight';
};

export type ExternalPreviewInsertionAcknowledgement = {
	onAcknowledged: () => void;
	onNotAcknowledged?: (result: ExternalPreviewInsertionNotAcknowledged) => void;
};

export type OpenRemixPreviewOptions = {
	isFloating: boolean;
	remixConversationId?: string;
	/**
	 * Invariant: invoke only after the preview surface actually opened (e.g. the staging modal's
	 * `onOpenCallback`), never when opening was merely requested. Lifecycle ownership settles on it.
	 * Integrations without a real open signal must leave it uncalled; the caller bounds the wait.
	 */
	onOpen?: () => void;
	onSubmit?: () => void;
	/**
	 * Present only when a retained lifecycle observes the external preview's editor insertion.
	 * `onAcknowledged` fires once after the finalize surface positively acknowledges durable insertion.
	 * `onNotAcknowledged` is diagnostics only: it reports the classified reason an attempt got no
	 * positive acknowledgement and never settles ownership.
	 */
	editorInsertionAcknowledgement?: ExternalPreviewInsertionAcknowledgement;
	onDismiss?: () => void;
};

export type RemixInsertSuccessPayload = {
	currentConversationId?: string;
	remixMetadata?: RemixFeedbackMetadata;
	requestId?: string;
};

export type RemixFinalizeFailedPayload = {
	/** The remixed whiteboard's content id (parsed from the embed URL). */
	contentId: string;
	/** The `~ROVO…` embed URL currently present in the document. */
	originalUrl: string;
};

export type RemixFinalizeSuccessPayload = {
	/** The remixed whiteboard's content id. */
	contentId: string;
	/** The parent used when the whiteboard was finalized. Root-level content has no parent. */
	parentId: string | undefined;
};

/**
 * Terminal result of a bounded conversation-assistant handoff. Only `acknowledged` proves the
 * requested conversation is showing; every other outcome is a distinct reason no proof arrived.
 * Negative outcomes release shadow ownership as an unacknowledged handoff (`end('abandoned')`)
 * and nothing else: no `handed-off`, no `CANCELLED`, no producer abort, no product failure, and no
 * cancellation UX.
 */
export type HandoffAcknowledgementResult =
	| { outcome: 'acknowledged' }
	| { outcome: 'timed-out' }
	| { outcome: 'rejected' }
	| { outcome: 'abandoned'; reason: 'superseded' | 'unmounted' };

export type RemixProductIntegration = {
	closeConversationAssistant?: () => void;
	closeRemixPreview: () => void;
	contentType: string;
	sourceContent?: {
		contentId?: string;
		contentType?: string;
	};
	isRemixPreviewVisible: boolean;
	onRemixInsertSuccess?: (payload: RemixInsertSuccessPayload) => void;
	/**
	 * Fired after automatic finalization moves a remixed whiteboard into the
	 * product space.
	 */
	onFinalizeSuccess?: (payload: RemixFinalizeSuccessPayload) => void;
	/**
	 * Fired when automatic finalization of a remixed whiteboard fails. Product
	 * integrations may take over the recovery UX (e.g. open a space picker) and
	 * return `true` to signal the failure was handled.
	 */
	onFinalizeFailed?: (payload: RemixFinalizeFailedPayload) => boolean;
	openRemixPreview: (options: OpenRemixPreviewOptions) => void;
	openConversationAssistant?: (location: string, conversationId?: string) => void;
	/**
	 * Resolves `acknowledged` only after the requested conversation-assistant surface is observed
	 * showing the requested conversation. Never rejects for an expected negative outcome.
	 */
	requestConversationAssistantOpen?: (
		location: string,
		conversationId?: string,
	) => Promise<HandoffAcknowledgementResult>;
	toggleConversationAssistant?: (location: string) => void;
};

export type RemixProductConfig = {
	analyticsProductName?: string;
	getRemixGeneratePreviewMetadata?: (
		selection: RemixOptionSelectionParameters,
	) => Record<string, boolean | number | string | undefined>;
	isActionReady?: (chatMessages: StoreChatItem[]) => boolean;
	isNodeUnsupportedForRemix?: (node: ADFEntity) => boolean;
	productKey: string;
	shouldUseEphemeralPreview?: (chatMessages: StoreChatItem[]) => boolean;
};
